import * as _ from 'underscore';
import { SearchSuggestionsService } from '~content/services/SearchSuggestionsService';
import { CollectionVisibility } from '~src/app/spaces/Collections/CollectionModels';
import {
    BackgroundJobStatus, BackgroundJobTaskType, BackgroundJobTimeRange
} from '~src/backgroundJob/types/BackgroundJobInfo';
import {
    getSessionManagementServiceInstance, SessionManagementService
} from '~src/sessionManagement/SessionManagementService';
import { ITextDropdownOption } from '~src/ts/directives/tbTextDropdown';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { DateHelpers } from '~src/ts/helpers/DateHelpers';
import { Helpers } from '~src/ts/helpers/Helpers';
import { SiteRoleFilterHelper } from '~src/ts/helpers/SiteRoleFilterHelper';
import { ContentFilterKeyName } from '~src/ts/models/ContentFilters';
import { DataAccelerationStatus } from '~src/ts/models/DataAccelerationStatusInfo';
import { FlowOutputStepRunStatus } from '~src/ts/models/FlowOutputStepRunInfo';
import { IFieldValue } from '~src/ts/ServerApi/types/IFieldValue';
import { FilterFieldNames } from '~src/ts/ServerApi/types/IFilterClause';
import { SiteRoles } from '~src/ts/ServerApi/types/ISiteRole';
import { FilterValueConstants } from '~src/ts/services/FilterValueConstants';
import { SiteRolesResource } from '~src/userManagement/services/resources/SiteRoles';

export type FilterCategory = 'general' | 'datasource' | 'project' | 'dataAcceleration';
export type FilterType = 'typeahead' | 'boolean' | 'text' | 'textDropdown' | 'date' | 'select';

export interface IFilterDefinition<TFilterValue> {
  name: ContentFilterKeyName;
  type: FilterType;
  category: FilterCategory;

  // These functions map a filter value to and from $location.search() params.
  valueToParams: (value: TFilterValue) => any;
  valueFromParams: (params: any) => Promise<TFilterValue>;
}

export interface ITypeaheadFilterDefinition extends IFilterDefinition<IFieldValue> {
  field: string;
  matchPrefixesOnly: boolean;
}

export interface ISelectFilterDefinitionOption<TFilterValue> {
  translate: string;
  value: TFilterValue;
  count?: number;
}

export interface ISelectFilterDefinition<TFilterValue> extends IFilterDefinition<TFilterValue> {
  options: ISelectFilterDefinitionOption<TFilterValue>[];
}

interface ITextDropdownFilterDefition<TFilterValue> extends ISelectFilterDefinition<TFilterValue> {
  isValid: (val: string) => boolean;
}

interface ISelectFilterDefinitionMap<TFilterValue> extends _.Dictionary<TFilterValue> {
  '': TFilterValue; //This is the required default select option value
}

export enum PasswordStatus { Any, Embedded, NotEmbedded }
export enum DatasourceType { All = 1, Embedded = 2, Published = 3 }
export enum ExtractStatus {
  All = '',
  Live = 'live',
  Published = 'published',  // Currently only the workbook filter supports this option
  AllExtracts = 'allExtracts',
  UnencryptedExtracts = 'unencryptedExtracts',
  EncryptedExctacts = 'encryptedExtracts',
  CurrentlyEncrypting = 'currentlyEncrypting',
  CurrentlyDecrypting = 'currentlyDecrypting'
}

export namespace FilterPanelService {

  const SessionManagementService: SessionManagementService = getSessionManagementServiceInstance();

  function createFilterDefinitions() {
    return <{ [N in ContentFilterKeyName]: IFilterDefinition<any> }>_.indexBy(
      _.compact([

        // General Filters

        <ITypeaheadFilterDefinition>
        {
          name: 'project',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.projectId,
          valueFromParams: fromSingleParam('project', (paramValue: string) => SearchSuggestionsService.project(paramValue)),
          valueToParams: toSingleParam('project', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        !SessionManagementService.isLimitedUserVisibilityEnforced() && getContentOwnerFilter(),

        <ITypeaheadFilterDefinition>
        {
          name: 'tag',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.tags,
          valueFromParams: fromSingleParam('tag', (paramValue: string) => Promise.resolve({ value: paramValue, label: paramValue, count: 0 })),
          valueToParams: toSingleParam('tag', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        siteRoleFilterDefinition(),
        groupsMinimumSiteRoleDefinition(),
        maxSiteRoleDefinition(),

        afterDateFilterDefinition('createdAfter', 'general'),
        beforeDateFilterDefinition('createdBefore', 'general'),

        afterDateFilterDefinition('createdTimeAfter', 'general'),
        beforeDateFilterDefinition('createdTimeBefore', 'general'),

        afterDateFilterDefinition('modifiedAfter', 'general'),
        beforeDateFilterDefinition('modifiedBefore', 'general'),

        afterDateFilterDefinition('updatedTimeAfter', 'general'),
        beforeDateFilterDefinition('updatedTimeBefore', 'general'),

        afterDateFilterDefinition('startedAfter', 'general'),
        beforeDateFilterDefinition('startedBefore', 'general'),
        afterDateFilterDefinition('completedAfter', 'general'),
        beforeDateFilterDefinition('completedBefore', 'general'),

        booleanFilterDefinition('favorite', 'general'),
        booleanFilterDefinition('recent', 'general'),
        booleanFilterDefinition('hasAlert', 'general'),
        FeatureFlags.collectionsGAEnabled && booleanFilterDefinition('hideEmptyCollections', 'general'),
        FeatureFlags.personalSpaceEnabled && SessionManagementService.isSessionSiteOrServerAdmin() && booleanFilterDefinition('usersPersonalSpaceContent', 'general'),

        <ITypeaheadFilterDefinition>
        {
          name: 'flowOutputStep',
          type: 'typeahead',
          category: 'general',
          field: FilterFieldNames.flowOutputStepName,
          valueFromParams: fromSingleParam('flowOutputStep', (paramValue: string) => Promise.resolve({ value: paramValue, label: paramValue, count: 0 })),
          valueToParams: toSingleParam('flowOutputStep', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: false
        },

        selectFilterDefinition('status',
          {
            '':           '',
            'PENDING':    FlowOutputStepRunStatus.pending,
            'INPROGRESS': FlowOutputStepRunStatus.inProgress,
            'SUCCESS':    FlowOutputStepRunStatus.success,
            'FAILED':     FlowOutputStepRunStatus.failed,
// TODO tfs844917            'CANCELLED':  FlowOutputStepRunStatus.cancelled,
          },
          [
            { value: '',                                  translate: 'flowRunStatusAll' },
            { value: FlowOutputStepRunStatus.success,     translate: 'flowRunStatusSucceeded' },
            { value: FlowOutputStepRunStatus.failed,      translate: 'flowRunStatusFailed' },
            // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
 // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
            // TODO tfs844917            { value: FlowOutputStepRunStatus.cancelled,   translate: 'flowRunStatusCancelled'},
            { value: FlowOutputStepRunStatus.inProgress,  translate: 'flowRunStatusInProgress' },
            { value: FlowOutputStepRunStatus.pending,     translate: 'flowRunStatusPending' },
          ],
          'general'
        ),

        selectFilterDefinition('backgroundJobStatus',
          {
            '': '',
            'Pending': BackgroundJobStatus.Pending,
            'InProgress': BackgroundJobStatus.InProgress,
            'Completed': BackgroundJobStatus.Completed,
            'Failed': BackgroundJobStatus.Failed,
            'Cancelled': BackgroundJobStatus.Cancelled,
          },
          [
            { value: '', translate: 'backgroundJobStatusAll' },
            { value: BackgroundJobStatus.Pending, translate: 'backgroundJobStatusPending' },
            { value: BackgroundJobStatus.InProgress, translate: 'backgroundJobStatusInProgress' },
            { value: BackgroundJobStatus.Completed, translate: 'backgroundJobStatusCompleted' },
            { value: BackgroundJobStatus.Failed, translate: 'backgroundJobStatusFailed' },
            { value: BackgroundJobStatus.Cancelled, translate: 'backgroundJobStatusCancelled' },
          ],
          'general'
        ),

        selectFilterDefinition('dataAccelerationStatus',
          {
            '': '',
            'CanBeAccelerated': DataAccelerationStatus.CanBeAccelerated,
            'Accelerated': DataAccelerationStatus.Accelerated,
            'CannotBeAccelerated': DataAccelerationStatus.CannotBeAccelerated,
          },
          [
            { value: '', translate: 'dataAccelerationStatusAll' },
            { value: DataAccelerationStatus.CanBeAccelerated, translate: 'dataAccelerationStatusCanBeAccelerated' },
            { value: DataAccelerationStatus.Accelerated, translate: 'dataAccelerationStatusAccelerated' },
            { value: DataAccelerationStatus.CannotBeAccelerated, translate: 'dataAccelerationStatusCannotBeAccelerated' },
          ],
          'dataAcceleration'
        ),

        backgroundJobTaskTypeDefinition(),

        selectFilterDefinition('backgroundJobTimeRange',
        {
          '': '',
          '1': BackgroundJobTimeRange.One,
          '4': BackgroundJobTimeRange.Four,
          '8': BackgroundJobTimeRange.Eight,
          '12': BackgroundJobTimeRange.Twelve,
          '16': BackgroundJobTimeRange.Sixteen,
          '20': BackgroundJobTimeRange.Twenty,
        },
        [
          {value: BackgroundJobTimeRange.One, translate: 'backgroundJobTimeRange1h'},
          {value: BackgroundJobTimeRange.Four, translate: 'backgroundJobTimeRange4h'},
          {value: BackgroundJobTimeRange.Eight, translate: 'backgroundJobTimeRange8h'},
          {value: BackgroundJobTimeRange.Twelve, translate: 'backgroundJobTimeRange12h'},
          {value: BackgroundJobTimeRange.Sixteen, translate: 'backgroundJobTimeRange16h'},
          {value: BackgroundJobTimeRange.Twenty, translate: 'backgroundJobTimeRange20h'},
          {value: '', translate: 'backgroundJobTimeRange24h'},
        ],
        'general'
      ),


        booleanFilterDefinition('showSystemJobs', 'general'),

        selectFilterDefinition('collectionVisibility',
        {
          '': '',
          'public': CollectionVisibility.public,
          'private': CollectionVisibility.private,
        },
        [
          { value: '', translate: 'collectionVisibilityAllFilter' },
          { value: CollectionVisibility.public, translate: 'collectionVisibilityPublicFilter' },
          { value: CollectionVisibility.private, translate: 'collectionVisibilityPrivate' }
        ],
        'general'
      ),

        // Data Source Filters

        selectFilterDefinition('showEmbedded',
          { // param key values mapped to value states
            '': DatasourceType.Published,
            'true': DatasourceType.All,
            'only': DatasourceType.Embedded
          },
          [ // Options
            { value: DatasourceType.All,       translate: 'datasourceTypeAll' },
            { value: DatasourceType.Published, translate: 'datasourceTypePublishedOnly' },
            { value: DatasourceType.Embedded,  translate: 'datasourceTypeEmbeddedOnly' }
          ],
          'datasource'
        ),

        selectFilterDefinition('hasActiveWarning',
          {
            '': '',
            'false': 'false',
            'true': 'true',
          },
          [
            { value: '',      translate: 'hasActiveWarningAll' },
            { value: 'false', translate: 'hasActiveWarningFalse' },
            { value: 'true',  translate: 'hasActiveWarningTrue' },
          ],
          'datasource' // Only used for External Assets (Data Catalog)
        ),

        selectFilterDefinition('hasActiveDataQualityWarning', // DQW Filter with crass search enabled
          {
            '': '',
            'false': 'false',
            'true': 'true',
          },
          [
            { value: '',      translate: 'hasActiveWarningAll' },
            { value: 'false', translate: 'hasActiveWarningFalse' },
            { value: 'true',  translate: 'hasActiveWarningTrue' },
          ],
          'datasource'
        ),
        selectFilterDefinition('isEmbedded', // DQW Filter with crass search enabled
        {
          '': '',
          'false': 'false',
          'true': 'true',
        },
        [
          { value: '',      translate: 'isEmbeddedAll' },
          { value: 'false', translate: 'isEmbeddedFalse' },
          { value: 'true',  translate: 'isEmbeddedTrue' },
        ],
        'datasource'
      ),

        <ITypeaheadFilterDefinition>
        {
          name: 'connectionType',
          type: 'typeahead',
          category: 'datasource',
          field: FilterFieldNames.connectionType,
          valueFromParams: fromSingleParam('connectionType', (paramValue: string) => SearchSuggestionsService.dataConnectionType(paramValue)),
          valueToParams: toSingleParam('connectionType', (fieldValue: IFieldValue) => fieldValue.value),
          matchPrefixesOnly: true
        },

        textFilterDefinition('serverName', 'datasource'),

        textDropdownFilterDefinition('serverPort',
          [
            { value: null, translate: 'serverPortAny' },
            { value: FilterValueConstants.ServerPortDefault, translate: 'serverPortDefault' }
          ],
          (val: string) => (val === FilterValueConstants.ServerPortDefault
                               || (/^\d*$/.exec(val) && parseInt(val, 10) <= 65535)),
          'datasource'
        ),

        textFilterDefinition('databaseUsername', 'datasource'),

        selectFilterDefinition('hasPassword',
          { // param key values mapped to value states
            '': PasswordStatus.Any,
            'true': PasswordStatus.Embedded,
            'false': PasswordStatus.NotEmbedded
          },
          [ // Options
            { value: PasswordStatus.Any,         translate: 'hasPasswordAny' },
            { value: PasswordStatus.Embedded,    translate: 'hasPasswordEmbedded' },
            { value: PasswordStatus.NotEmbedded, translate: 'hasPasswordNotEmbedded' }
          ],
          'datasource'
        ),

        !SessionManagementService.isExtractEncryptionEnabledOmniSwitch() && booleanFilterDefinition('hasExtracts', 'datasource'),
        SessionManagementService.isExtractEncryptionEnabledOmniSwitch() && selectFilterDefinition('extractStatus',
          {
            '': ExtractStatus.All,
            'live': ExtractStatus.Live,
            // 'published' intentionally missing, the server-side filter translator currently does not support it
            'allExtracts': ExtractStatus.AllExtracts,
            'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
            'encryptedExtracts': ExtractStatus.EncryptedExctacts,
            'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
            'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
          },
          [
            // Options
            {value: ExtractStatus.All, translate: 'extractStatusAll'},
            {value: ExtractStatus.Live, translate: 'extractStatusLive'},
            {value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts'},
            {value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted'},
            {value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted'},
            {value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting'},
            {value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting'}
          ],
          'datasource'
        ),
        SessionManagementService.isExtractEncryptionEnabledOmniSwitch() && selectFilterDefinition('workbookConnFilter',
          {
            '': ExtractStatus.All,
            'live': ExtractStatus.Live,
            'published': ExtractStatus.Published,
            'allExtracts': ExtractStatus.AllExtracts,
            'unencryptedExtracts': ExtractStatus.UnencryptedExtracts,
            'encryptedExtracts': ExtractStatus.EncryptedExctacts,
            'currentlyEncrypting': ExtractStatus.CurrentlyEncrypting,
            'currentlyDecrypting': ExtractStatus.CurrentlyDecrypting
          },
          [
            // Options
            {value: ExtractStatus.All, translate: 'extractStatusAll'},
            {value: ExtractStatus.Live, translate: 'extractStatusLive'},
            {value: ExtractStatus.AllExtracts, translate: 'extractStatusExtracts'},
            {value: ExtractStatus.Published, translate: 'workbookConnFilterPublished'},
            {value: ExtractStatus.UnencryptedExtracts, translate: 'extractStatusUnencrypted'},
            {value: ExtractStatus.EncryptedExctacts, translate: 'extractStatusEncrypted'},
            {value: ExtractStatus.CurrentlyEncrypting, translate: 'extractStatusEncrypting'},
            {value: ExtractStatus.CurrentlyDecrypting, translate: 'extractStatusDecrypting'}
          ],
          'datasource'
        ),
        SessionManagementService.isExtractEncryptionEnabledOmniSwitch() && booleanFilterDefinition('includeLiveToDataEngine', 'datasource'),
        booleanFilterDefinition('isCertified', 'datasource')

      ]), 'name');
  }

  function siteRoleFilterDefinition() {
    const allSiteRolesExceptGuest = _.without(SiteRolesResource.instance.allOptions(), SiteRoles.Guest);
    return siteRoleSelectFilterDefinition('siteRole', allSiteRolesExceptGuest);
  }

  function groupsMinimumSiteRoleDefinition() {
    return siteRoleSelectFilterDefinition('groupsMinimumSiteRole', SiteRolesResource.instance.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator));
  }

  function backgroundJobTaskTypeDefinition() {
    let paramToValueMap = {
      '': '',
      'Bridge': BackgroundJobTaskType.Bridge,
      'Extract': BackgroundJobTaskType.Extract,
      'Flow': BackgroundJobTaskType.Flow,
      'Subscription': BackgroundJobTaskType.Subscription,
      'System': BackgroundJobTaskType.System,
      'Encryption': BackgroundJobTaskType.Encryption,
      'Acceleration': BackgroundJobTaskType.Acceleration,
    };
    let options = [
      { value: '', translate: 'backgroundJobTaskTypeAll' },
      { value: BackgroundJobTaskType.Bridge, translate: 'backgroundJobTaskTypeBridge' },
      { value: BackgroundJobTaskType.Extract, translate: 'backgroundJobTaskTypeExtract' },
      { value: BackgroundJobTaskType.Flow, translate: 'backgroundJobTaskTypeFlow' },
      { value: BackgroundJobTaskType.Subscription, translate: 'backgroundJobTaskTypeSubscription' },
      { value: BackgroundJobTaskType.System, translate: 'backgroundJobTaskTypeSystem' },
      { value: BackgroundJobTaskType.Encryption, translate: 'backgroundJobTaskTypeEncryption' },
      { value: BackgroundJobTaskType.Acceleration, translate: 'backgroundJobTaskTypeAcceleration'},
    ];

    // Non server admins cannot see the System  jobs option
    if (!SessionManagementService.isSessionServerAdmin()) {
      delete paramToValueMap.System;
      options = options.filter(o => o.value !== BackgroundJobTaskType.System);
    }
    return selectFilterDefinition('backgroundJobTaskType', paramToValueMap, options, 'general');
  }

  function maxSiteRoleDefinition() {
    return siteRoleSelectFilterDefinition('maxSiteRole', SiteRolesResource.instance.optionsForCurrentUser());
  }

  function siteRoleSelectFilterDefinition(name: ContentFilterKeyName, roles: string[]) {
    return selectFilterDefinition(name,

      // param key values mapped to value states
      // Map '' to "any role"
      _.extend({}, SiteRoles, { '': SiteRoleFilterHelper.AnyRole }),

      SiteRoleFilterHelper.siteRoleOptions(roles),
      'general'
    );
  }

  function selectFilterDefinition<TFilterValue>(
    name: ContentFilterKeyName,
    paramToValueMap: ISelectFilterDefinitionMap<TFilterValue>,
    options: ISelectFilterDefinitionOption<TFilterValue>[],
    category?: FilterCategory
  ): ISelectFilterDefinition<TFilterValue> {
    const valueToParamMap = Helpers.swapDict(paramToValueMap);
    return {
      name: name,
      type: 'select',
      category: category,
      valueFromParams: (params: any) => Promise.resolve(paramToValueMap[params[name]] || paramToValueMap['']), //Bypass fromSingleParam to handle the undefined param case
      valueToParams: toSingleParam(name, (val: TFilterValue) => (valueToParamMap[val + ''] || null)),
      options: options
    };
  }

  function booleanFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<boolean> {
    return {
      name: name,
      type: 'boolean',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => Promise.resolve(Helpers.toBoolean(paramValue))),
      valueToParams: toSingleParam(name, (value: boolean) => '' + value)
    };
  }

  function getContentOwnerFilter(): ITypeaheadFilterDefinition {
    return  {
      name: 'owner',
      type: 'typeahead',
      category: 'general',
      field: FilterFieldNames.ownerId,
      valueFromParams: fromSingleParam('owner', (paramValue: string) => SearchSuggestionsService.user(paramValue)),
      valueToParams: toSingleParam('owner', (fieldValue: IFieldValue) => fieldValue.value),
      matchPrefixesOnly: true
    };
  }

  function textFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<string> {
    return {
      name: name,
      type: 'text',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => Promise.resolve(paramValue)),
      valueToParams: toSingleParam(name, (text: string) => text)
    };
  }

  function textDropdownFilterDefinition(
    name: ContentFilterKeyName,
    options: ITextDropdownOption[],
    isValid: (val: string) => boolean,
    category?: FilterCategory
  ): ITextDropdownFilterDefition<string> {
    return {
      name: name,
      type: 'textDropdown',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => Promise.resolve(isValid(paramValue) ? paramValue : null)),
      valueToParams: toSingleParam(name, (text: string) => text),
      options: options,
      isValid: isValid
    };
  }

  function afterDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => dateFromISOString(paramValue)),
      valueToParams: toSingleParam(name, (date: Date) => DateHelpers.toISOString(date))
    };
  }

  function beforeDateFilterDefinition(name: ContentFilterKeyName, category?: FilterCategory): IFilterDefinition<Date> {
    return {
      name: name,
      type: 'date',
      category: category,
      valueFromParams: fromSingleParam(name, (paramValue: string) => dateFromISOString(paramValue)),
      valueToParams: toSingleParam(name, (date: Date) => DateHelpers.toISOString(DateHelpers.endOfDay(date)))
    };
  }

  function dateFromISOString(paramValue: string): Promise<Date> {
    return Promise.resolve(DateHelpers.midnight(DateHelpers.fromISOString(paramValue)));
  }

  function fromSingleParam<TFilterValue>(paramName: ContentFilterKeyName, valueFn: (paramValue: string) => Promise<TFilterValue>) {
    return (params: any) => params[paramName] ? valueFn(params[paramName]) : Promise.resolve(null);
  }

  function toSingleParam<TFilterValue>(paramName: ContentFilterKeyName, paramValueFn: (value: TFilterValue) => string) {
    return (value: TFilterValue) => {
      const params: any = {};
      params[paramName] = value ? paramValueFn(value) : null;
      return params;
    };
  }

  export function getFilterDefinitions(filterNames: ContentFilterKeyName[]): IFilterDefinition<any>[] {
    const filterDefinitions: { [N in ContentFilterKeyName]: IFilterDefinition<any> } = createFilterDefinitions();
    const defns = _.map(filterNames, (filterName: ContentFilterKeyName) => filterDefinitions[filterName]);
    return _.filter(defns, (defn: IFilterDefinition<any>) => defn != null);
  }

}

export type FilterPanelService = typeof FilterPanelService;
